/**
 * Pure helpers for cleaning raw wedge-scanned strings.
 *
 * The BT-A600 delivers a barcode as HID keystrokes into the focused field,
 * terminated by an Enter suffix (consumed by the field's onPressEnter). The raw
 * value can still carry trailing control characters and, if the device is
 * configured to transmit AIM symbology identifiers, a 3-character prefix such as
 * `]C1` (Code128), `]d2` (DataMatrix) or `]Q3` (QR).
 */

export interface ScanCleanOptions {
  /**
   * Strip a leading AIM symbology identifier (`]` + code char + modifier char).
   * Off by default — most deployments do NOT transmit AIM identifiers, and
   * stripping unconditionally would corrupt a real barcode that happens to
   * start with `]`.
   */
  stripSymbologyPrefix?: boolean;
  /** Upper-case the result (some flows compare case-insensitively). */
  toUpperCase?: boolean;
}

// AIM identifier: "]" followed by a code character and a modifier character.
const AIM_SYMBOLOGY_PREFIX = /^\][A-Za-z][0-9A-Za-z]/;

/** Trim surrounding whitespace / wedge control chars and apply optional cleanups. */
export function cleanScan(
  raw: string | null | undefined,
  options: ScanCleanOptions = {}
): string {
  let value = (raw ?? "").replace(/[\r\n\t]+/g, "").trim();

  if (options.stripSymbologyPrefix && AIM_SYMBOLOGY_PREFIX.test(value)) {
    value = value.slice(3);
  }

  if (options.toUpperCase) {
    value = value.toUpperCase();
  }

  return value;
}

/** True when a cleaned scan carries no usable payload. */
export function isBlankScan(raw: string | null | undefined): boolean {
  return cleanScan(raw) === "";
}
